// src/components/QuestionAdvancedSettings.tsx

import React, { useState } from 'react';
import { Question, QuestionTriggers, QuestionConditions } from '../types/companyQuestions';

interface QuestionAdvancedSettingsProps {
  question: Question;
  allQuestions: Question[];
  onUpdate: (updates: Partial<Question>) => void;
}

const QuestionAdvancedSettings: React.FC<QuestionAdvancedSettingsProps> = ({
  question,
  allQuestions,
  onUpdate,
}) => {
  const [activeTab, setActiveTab] = useState<'triggers' | 'conditions' | 'validation'>('triggers');

  // Helper to get all question options from allQuestions
  const getQuestionById = (qId: string) => allQuestions.find((q) => q.id === qId);

  return (
    <div style={{ gridColumn: '1 / -1', marginTop: '15px' }}>
      <div
        style={{
          border: '1px solid #dee2e6',
          borderRadius: '5px',
          overflow: 'hidden',
        }}
      >
        {/* Tabs */}
        <div style={{ display: 'flex', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
          <button
            onClick={() => setActiveTab('triggers')}
            style={{
              flex: 1,
              padding: '10px',
              border: 'none',
              backgroundColor: activeTab === 'triggers' ? '#007bff' : 'transparent',
              color: activeTab === 'triggers' ? 'white' : '#333',
              cursor: 'pointer',
              fontWeight: activeTab === 'triggers' ? 'bold' : 'normal',
              fontSize: '13px',
            }}
            disabled={question.type !== 'single_choice' && question.type !== 'multi_choice'}
          >
            Triggers {(question.type !== 'single_choice' && question.type !== 'multi_choice') && '(N/A)'}
          </button>
          <button
            onClick={() => setActiveTab('conditions')}
            style={{
              flex: 1,
              padding: '10px',
              border: 'none',
              backgroundColor: activeTab === 'conditions' ? '#007bff' : 'transparent',
              color: activeTab === 'conditions' ? 'white' : '#333',
              cursor: 'pointer',
              fontWeight: activeTab === 'conditions' ? 'bold' : 'normal',
              fontSize: '13px',
            }}
          >
            Conditions
          </button>
          <button
            onClick={() => setActiveTab('validation')}
            style={{
              flex: 1,
              padding: '10px',
              border: 'none',
              backgroundColor: activeTab === 'validation' ? '#007bff' : 'transparent',
              color: activeTab === 'validation' ? 'white' : '#333',
              cursor: 'pointer',
              fontWeight: activeTab === 'validation' ? 'bold' : 'normal',
              fontSize: '13px',
            }}
            disabled={question.type !== 'number' && question.type !== 'text'}
          >
            Validation {(question.type !== 'number' && question.type !== 'text') && '(N/A)'}
          </button>
        </div>

        {/* Tab Content */}
        <div style={{ padding: '15px', backgroundColor: 'white' }}>
          {activeTab === 'triggers' && (question.type === 'single_choice' || question.type === 'multi_choice') && (
            <TriggersEditor question={question} allQuestions={allQuestions} onUpdate={onUpdate} />
          )}

          {activeTab === 'conditions' && (
            <ConditionsEditor question={question} allQuestions={allQuestions} onUpdate={onUpdate} />
          )}

          {activeTab === 'validation' && (question.type === 'number' || question.type === 'text') && (
            <ValidationEditor question={question} onUpdate={onUpdate} />
          )}
        </div>
      </div>
    </div>
  );
};

// ===== Triggers Editor =====
interface TriggersEditorProps {
  question: Question;
  allQuestions: Question[];
  onUpdate: (updates: Partial<Question>) => void;
}

const TriggersEditor: React.FC<TriggersEditorProps> = ({ question, allQuestions, onUpdate }) => {
  const triggers = question.triggers || {};
  const options = question.options || [];

  const addTrigger = (optionValue: string, questionId: string) => {
    const newTriggers = { ...triggers };
    if (!newTriggers[optionValue]) {
      newTriggers[optionValue] = [];
    }
    if (!newTriggers[optionValue].includes(questionId)) {
      newTriggers[optionValue].push(questionId);
    }
    onUpdate({ triggers: newTriggers });
  };

  const removeTrigger = (optionValue: string, questionId: string) => {
    const newTriggers = { ...triggers };
    if (newTriggers[optionValue]) {
      newTriggers[optionValue] = newTriggers[optionValue].filter((id) => id !== questionId);
      if (newTriggers[optionValue].length === 0) {
        delete newTriggers[optionValue];
      }
    }
    onUpdate({ triggers: Object.keys(newTriggers).length > 0 ? newTriggers : undefined });
  };

  return (
    <div>
      <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
        Specify which questions should be asked based on the answer to this question.
      </p>

      {options.length === 0 ? (
        <p style={{ fontSize: '13px', color: '#999', fontStyle: 'italic' }}>
          Please add options to this question first.
        </p>
      ) : (
        options.map((option) => (
          <div
            key={option}
            style={{
              marginBottom: '15px',
              padding: '10px',
              backgroundColor: '#f8f9fa',
              borderRadius: '5px',
              border: '1px solid #dee2e6',
            }}
          >
            <div style={{ fontWeight: 'bold', fontSize: '13px', marginBottom: '8px' }}>
              When user selects: "{option}"
            </div>

            {/* List existing triggers */}
            {triggers[option] && triggers[option].length > 0 && (
              <div style={{ marginBottom: '10px' }}>
                {triggers[option].map((qId) => {
                  const targetQ = allQuestions.find((q) => q.id === qId);
                  return (
                    <div
                      key={qId}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '5px 8px',
                        backgroundColor: '#e7f3ff',
                        borderRadius: '3px',
                        marginBottom: '5px',
                        fontSize: '12px',
                      }}
                    >
                      <span>
                        Ask: <strong>{targetQ?.text || qId}</strong> ({qId})
                      </span>
                      <button
                        onClick={() => removeTrigger(option, qId)}
                        style={{
                          padding: '2px 8px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer',
                          fontSize: '11px',
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Add new trigger */}
            <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              <select
                onChange={(e) => {
                  if (e.target.value) {
                    addTrigger(option, e.target.value);
                    e.target.value = '';
                  }
                }}
                style={{
                  flex: 1,
                  padding: '5px',
                  fontSize: '12px',
                  borderRadius: '3px',
                  border: '1px solid #ced4da',
                }}
              >
                <option value="">+ Add question to trigger</option>
                {allQuestions
                  .filter((q) => q.id !== question.id)
                  .map((q) => (
                    <option key={q.id} value={q.id}>
                      {q.text} ({q.id})
                    </option>
                  ))}
              </select>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// ===== Conditions Editor =====
interface ConditionsEditorProps {
  question: Question;
  allQuestions: Question[];
  onUpdate: (updates: Partial<Question>) => void;
}

const ConditionsEditor: React.FC<ConditionsEditorProps> = ({ question, allQuestions, onUpdate }) => {
  const conditions = question.conditions || {};
  const showIf = conditions.show_if || {};
  const skipIf = conditions.skip_if || {};
  const showIfQualified = conditions.show_if_qualified || false;

  const addShowIfCondition = (questionId: string, value: string) => {
    const newShowIf = { ...showIf };
    if (!newShowIf[questionId]) {
      newShowIf[questionId] = [];
    }
    const values = Array.isArray(newShowIf[questionId]) ? newShowIf[questionId] : [newShowIf[questionId]];
    if (!values.includes(value)) {
      values.push(value);
    }
    newShowIf[questionId] = values;
    onUpdate({
      conditions: { ...conditions, show_if: newShowIf },
    });
  };

  const removeShowIfCondition = (questionId: string, value: string) => {
    const newShowIf = { ...showIf };
    if (newShowIf[questionId]) {
      const values = Array.isArray(newShowIf[questionId]) ? newShowIf[questionId] : [newShowIf[questionId]];
      newShowIf[questionId] = values.filter((v: string) => v !== value);
      if (newShowIf[questionId].length === 0) {
        delete newShowIf[questionId];
      }
    }
    const hasConditions = Object.keys(newShowIf).length > 0 || Object.keys(skipIf).length > 0 || showIfQualified;
    onUpdate({
      conditions: hasConditions ? { ...conditions, show_if: Object.keys(newShowIf).length > 0 ? newShowIf : undefined } : null,
    });
  };

  const addSkipIfCondition = (questionId: string, value: string) => {
    const newSkipIf = { ...skipIf };
    if (!newSkipIf[questionId]) {
      newSkipIf[questionId] = [];
    }
    if (!newSkipIf[questionId].includes(value)) {
      newSkipIf[questionId].push(value);
    }
    onUpdate({
      conditions: { ...conditions, skip_if: newSkipIf },
    });
  };

  const removeSkipIfCondition = (questionId: string, value: string) => {
    const newSkipIf = { ...skipIf };
    if (newSkipIf[questionId]) {
      newSkipIf[questionId] = newSkipIf[questionId].filter((v) => v !== value);
      if (newSkipIf[questionId].length === 0) {
        delete newSkipIf[questionId];
      }
    }
    const hasConditions = Object.keys(showIf).length > 0 || Object.keys(newSkipIf).length > 0 || showIfQualified;
    onUpdate({
      conditions: hasConditions ? { ...conditions, skip_if: Object.keys(newSkipIf).length > 0 ? newSkipIf : undefined } : null,
    });
  };

  const toggleShowIfQualified = () => {
    const newValue = !showIfQualified;
    const hasConditions = Object.keys(showIf).length > 0 || Object.keys(skipIf).length > 0 || newValue;
    onUpdate({
      conditions: hasConditions ? { ...conditions, show_if_qualified: newValue } : null,
    });
  };

  const [selectedShowIfQuestion, setSelectedShowIfQuestion] = useState('');
  const [selectedShowIfValue, setSelectedShowIfValue] = useState('');
  const [selectedShowIfOperator, setSelectedShowIfOperator] = useState('>');
  const [selectedSkipIfQuestion, setSelectedSkipIfQuestion] = useState('');
  const [selectedSkipIfValue, setSelectedSkipIfValue] = useState('');
  const [selectedSkipIfOperator, setSelectedSkipIfOperator] = useState('>');

  const selectedShowQ = allQuestions.find((q) => q.id === selectedShowIfQuestion);
  const selectedSkipQ = allQuestions.find((q) => q.id === selectedSkipIfQuestion);

  return (
    <div>
      <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
        Control when this question should be shown or skipped based on answers to other questions.
      </p>

      {/* Show If */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ fontSize: '14px', marginBottom: '10px' }}>Show this question if:</h4>

        {/* Existing show_if conditions */}
        {Object.keys(showIf).length > 0 && (
          <div style={{ marginBottom: '10px' }}>
            {Object.entries(showIf).map(([qId, values]) => {
              const targetQ = allQuestions.find((q) => q.id === qId);
              const valueArray = Array.isArray(values) ? values : [values];
              return (
                <div key={qId} style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
                    {targetQ?.text || qId} equals:
                  </div>
                  {valueArray.map((val) => (
                    <div
                      key={val}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 8px',
                        backgroundColor: '#d4edda',
                        borderRadius: '3px',
                        marginRight: '5px',
                        marginBottom: '5px',
                        fontSize: '12px',
                      }}
                    >
                      <span>{val}</span>
                      <button
                        onClick={() => removeShowIfCondition(qId, val)}
                        style={{
                          marginLeft: '8px',
                          padding: '0 4px',
                          backgroundColor: 'transparent',
                          color: '#721c24',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Add new show_if */}
        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          <select
            value={selectedShowIfQuestion}
            onChange={(e) => {
              setSelectedShowIfQuestion(e.target.value);
              setSelectedShowIfValue('');
              setSelectedShowIfOperator('>');
            }}
            style={{ flex: 1, minWidth: '200px', padding: '6px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ced4da' }}
          >
            <option value="">Select a question</option>
            {allQuestions
              .filter((q) => q.id !== question.id)
              .map((q) => (
                <option key={q.id} value={q.id}>
                  {q.text} ({q.type})
                </option>
              ))}
          </select>

          {selectedShowIfQuestion && selectedShowQ && (
            <>
              {/* For choice questions - show option dropdown */}
              {(selectedShowQ.type === 'single_choice' || selectedShowQ.type === 'multi_choice') && (
                <select
                  value={selectedShowIfValue}
                  onChange={(e) => setSelectedShowIfValue(e.target.value)}
                  style={{ flex: 1, minWidth: '150px', padding: '6px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ced4da' }}
                >
                  <option value="">Select a value</option>
                  {selectedShowQ.options?.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              )}

              {/* For number questions - show operator + number input */}
              {selectedShowQ.type === 'number' && (
                <>
                  <select
                    value={selectedShowIfOperator}
                    onChange={(e) => setSelectedShowIfOperator(e.target.value)}
                    style={{ width: '80px', padding: '6px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ced4da' }}
                  >
                    <option value=">">{'>'}</option>
                    <option value=">=">{'≥'}</option>
                    <option value="<">{'<'}</option>
                    <option value="<=">{'≤'}</option>
                    <option value="==">{'='}</option>
                  </select>
                  <input
                    type="number"
                    value={selectedShowIfValue}
                    onChange={(e) => setSelectedShowIfValue(e.target.value)}
                    placeholder="Enter value"
                    style={{ flex: 1, minWidth: '100px', padding: '6px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ced4da' }}
                  />
                </>
              )}

              {/* For text questions - show text input */}
              {selectedShowQ.type === 'text' && (
                <input
                  type="text"
                  value={selectedShowIfValue}
                  onChange={(e) => setSelectedShowIfValue(e.target.value)}
                  placeholder="Enter value"
                  style={{ flex: 1, minWidth: '150px', padding: '6px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ced4da' }}
                />
              )}

              <button
                onClick={() => {
                  if (selectedShowIfQuestion && selectedShowIfValue) {
                    // For number questions, combine operator and value
                    const finalValue = selectedShowQ.type === 'number'
                      ? `${selectedShowIfOperator} ${selectedShowIfValue}`
                      : selectedShowIfValue;
                    addShowIfCondition(selectedShowIfQuestion, finalValue);
                    setSelectedShowIfQuestion('');
                    setSelectedShowIfValue('');
                    setSelectedShowIfOperator('>');
                  }
                }}
                disabled={!selectedShowIfValue}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: selectedShowIfValue ? 'pointer' : 'not-allowed',
                  fontSize: '12px',
                }}
              >
                + Add
              </button>
            </>
          )}
        </div>
      </div>

      {/* Skip If */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ fontSize: '14px', marginBottom: '10px' }}>Skip this question if:</h4>

        {/* Existing skip_if conditions */}
        {Object.keys(skipIf).length > 0 && (
          <div style={{ marginBottom: '10px' }}>
            {Object.entries(skipIf).map(([qId, values]) => {
              const targetQ = allQuestions.find((q) => q.id === qId);
              return (
                <div key={qId} style={{ marginBottom: '10px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '5px' }}>
                    {targetQ?.text || qId} equals:
                  </div>
                  {values.map((val) => (
                    <div
                      key={val}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '4px 8px',
                        backgroundColor: '#f8d7da',
                        borderRadius: '3px',
                        marginRight: '5px',
                        marginBottom: '5px',
                        fontSize: '12px',
                      }}
                    >
                      <span>{val}</span>
                      <button
                        onClick={() => removeSkipIfCondition(qId, val)}
                        style={{
                          marginLeft: '8px',
                          padding: '0 4px',
                          backgroundColor: 'transparent',
                          color: '#721c24',
                          border: 'none',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: 'bold',
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* Add new skip_if */}
        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
          <select
            value={selectedSkipIfQuestion}
            onChange={(e) => {
              setSelectedSkipIfQuestion(e.target.value);
              setSelectedSkipIfValue('');
              setSelectedSkipIfOperator('>');
            }}
            style={{ flex: 1, minWidth: '200px', padding: '6px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ced4da' }}
          >
            <option value="">Select a question</option>
            {allQuestions
              .filter((q) => q.id !== question.id)
              .map((q) => (
                <option key={q.id} value={q.id}>
                  {q.text} ({q.type})
                </option>
              ))}
          </select>

          {selectedSkipIfQuestion && selectedSkipQ && (
            <>
              {/* For choice questions - show option dropdown */}
              {(selectedSkipQ.type === 'single_choice' || selectedSkipQ.type === 'multi_choice') && (
                <select
                  value={selectedSkipIfValue}
                  onChange={(e) => setSelectedSkipIfValue(e.target.value)}
                  style={{ flex: 1, minWidth: '150px', padding: '6px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ced4da' }}
                >
                  <option value="">Select a value</option>
                  {selectedSkipQ.options?.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              )}

              {/* For number questions - show operator + number input */}
              {selectedSkipQ.type === 'number' && (
                <>
                  <select
                    value={selectedSkipIfOperator}
                    onChange={(e) => setSelectedSkipIfOperator(e.target.value)}
                    style={{ width: '80px', padding: '6px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ced4da' }}
                  >
                    <option value=">">{'>'}</option>
                    <option value=">=">{'≥'}</option>
                    <option value="<">{'<'}</option>
                    <option value="<=">{'≤'}</option>
                    <option value="==">{'='}</option>
                  </select>
                  <input
                    type="number"
                    value={selectedSkipIfValue}
                    onChange={(e) => setSelectedSkipIfValue(e.target.value)}
                    placeholder="Enter value"
                    style={{ flex: 1, minWidth: '100px', padding: '6px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ced4da' }}
                  />
                </>
              )}

              {/* For text questions - show text input */}
              {selectedSkipQ.type === 'text' && (
                <input
                  type="text"
                  value={selectedSkipIfValue}
                  onChange={(e) => setSelectedSkipIfValue(e.target.value)}
                  placeholder="Enter value"
                  style={{ flex: 1, minWidth: '150px', padding: '6px', fontSize: '12px', borderRadius: '3px', border: '1px solid #ced4da' }}
                />
              )}

              <button
                onClick={() => {
                  if (selectedSkipIfQuestion && selectedSkipIfValue) {
                    // For number questions, combine operator and value
                    const finalValue = selectedSkipQ.type === 'number'
                      ? `${selectedSkipIfOperator} ${selectedSkipIfValue}`
                      : selectedSkipIfValue;
                    addSkipIfCondition(selectedSkipIfQuestion, finalValue);
                    setSelectedSkipIfQuestion('');
                    setSelectedSkipIfValue('');
                    setSelectedSkipIfOperator('>');
                  }
                }}
                disabled={!selectedSkipIfValue}
                style={{
                  padding: '6px 12px',
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '3px',
                  cursor: selectedSkipIfValue ? 'pointer' : 'not-allowed',
                  fontSize: '12px',
                }}
              >
                + Add
              </button>
            </>
          )}
        </div>
      </div>

      {/* Show if Qualified */}
      <div>
        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '13px' }}>
          <input
            type="checkbox"
            checked={showIfQualified}
            onChange={toggleShowIfQualified}
            style={{ marginRight: '8px' }}
          />
          Only show this question if user is qualified
        </label>
      </div>
    </div>
  );
};

// ===== Validation Editor =====
interface ValidationEditorProps {
  question: Question;
  onUpdate: (updates: Partial<Question>) => void;
}

const ValidationEditor: React.FC<ValidationEditorProps> = ({ question, onUpdate }) => {
  const validation = question.validation || {};

  return (
    <div>
      <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
        Set minimum and maximum constraints for this question.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>
            Minimum Value
          </label>
          <input
            type="number"
            value={validation.min ?? ''}
            onChange={(e) =>
              onUpdate({
                validation: {
                  ...validation,
                  min: e.target.value ? parseInt(e.target.value) : undefined,
                },
              })
            }
            placeholder="No minimum"
            style={{
              width: '100%',
              padding: '6px',
              borderRadius: '3px',
              border: '1px solid #ced4da',
              fontSize: '13px',
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold' }}>
            Maximum Value
          </label>
          <input
            type="number"
            value={validation.max ?? ''}
            onChange={(e) =>
              onUpdate({
                validation: {
                  ...validation,
                  max: e.target.value ? parseInt(e.target.value) : undefined,
                },
              })
            }
            placeholder="No maximum"
            style={{
              width: '100%',
              padding: '6px',
              borderRadius: '3px',
              border: '1px solid #ced4da',
              fontSize: '13px',
            }}
          />
        </div>
      </div>

      {validation.min !== undefined && validation.max !== undefined && (
        <div
          style={{
            marginTop: '10px',
            padding: '8px',
            backgroundColor: '#e7f3ff',
            borderRadius: '3px',
            fontSize: '12px',
            color: '#004085',
          }}
        >
          Valid range: {validation.min} to {validation.max}
        </div>
      )}
    </div>
  );
};

export default QuestionAdvancedSettings;
